import React, { useState, useEffect } from 'react';

// ── Stylish SVGs for Product Highlights ──

const WinConditionSVG = () => (
  <svg viewBox="0 0 400 300" style={{ width: '100%', height: 'auto', dropShadow: '0 20px 40px rgba(0,0,0,0.5)' }}>
    <defs>
      <linearGradient id="bgGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#1e2235" />
        <stop offset="100%" stopColor="#141628" />
      </linearGradient>
      <filter id="glowHex">
        <feGaussianBlur stdDeviation="3" result="blur" />
        <feMerge>
          <feMergeNode in="blur"/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>
    </defs>
    <rect width="400" height="300" rx="20" fill="url(#bgGrad)" />
    
    {/* Grid Background Concept */}
    <g transform="translate(200, 150) scale(1.2)">
      {[-2,-1,0,1,2].map(r => 
        [-2,-1,0,1,2].map(q => {
          if (Math.abs(q + r) > 2) return null;
          const x = 30 * Math.sqrt(3) * (q + (r % 2) * 0.5);
          const y = 30 * (3 / 2 * r);
          // Highlight an orange path from top to bottom
          const isPath = (q===0 && r===-2) || (q===0 && r===-1) || (q===1 && r===0) || (q===0 && r===1) || (q===0 && r===2);
          const fill = isPath ? '#FF8C00' : '#2A2E45';
          const points = "0,-28 24,-14 24,14 0,28 -24,14 -24,-14";
          return (
            <polygon 
              key={`${q}-${r}`} 
              points={points} 
              transform={`translate(${x},${y}) scale(0.9)`} 
              fill={fill} 
              stroke={isPath ? '#FFE0B2' : '#3A4060'} 
              strokeWidth={isPath ? 2 : 1}
              filter={isPath ? "url(#glowHex)" : undefined}
            />
          );
        })
      )}
    </g>
    
    {/* Borders */}
    <rect x="-10" y="-10" width="420" height="20" fill="#FF8C00" opacity="0.8" filter="url(#glowHex)" />
    <rect x="-10" y="290" width="420" height="20" fill="#FF8C00" opacity="0.8" filter="url(#glowHex)" />
  </svg>
);

const BuzzerSVG = () => (
  <svg viewBox="0 0 400 300" style={{ width: '100%', height: 'auto' }}>
    <defs>
      <radialGradient id="ringGrad" cx="50%" cy="50%" r="50%">
        <stop offset="70%" stopColor="#ff5252" stopOpacity="0" />
        <stop offset="100%" stopColor="#ff5252" stopOpacity="0.8" />
      </radialGradient>
      <filter id="shadowRed">
        <feDropShadow dx="0" dy="10" stdDeviation="15" floodColor="#ff5252" floodOpacity="0.6"/>
      </filter>
    </defs>
    <rect width="400" height="300" rx="20" fill="#141628" />
    
    {/* Animated Rings */}
    <circle cx="200" cy="150" r="100" fill="url(#ringGrad)">
      <animate attributeName="r" values="80;130;80" dur="2s" repeatCount="indefinite" />
      <animate attributeName="opacity" values="0.8;0;0.8" dur="2s" repeatCount="indefinite" />
    </circle>
    <circle cx="200" cy="150" r="70" fill="url(#ringGrad)">
      <animate attributeName="r" values="70;90;70" dur="2s" repeatCount="indefinite" delay="0.5s" />
    </circle>
    
    {/* Main Button */}
    <circle cx="200" cy="150" r="60" fill="#ff5252" filter="url(#shadowRed)" />
  </svg>
);

const ControlSVG = () => (
  <svg viewBox="0 0 400 300" style={{ width: '100%', height: 'auto' }}>
    <rect width="400" height="300" rx="20" fill="#181A2A" />
    
    {/* Sidebar mock */}
    <rect x="250" y="40" width="110" height="220" rx="10" fill="#22263A" />
    <rect x="260" y="55" width="90" height="15" rx="4" fill="#3B405A" />
    <rect x="260" y="80" width="90" height="10" rx="3" fill="#313650" />
    <rect x="260" y="95" width="70" height="10" rx="3" fill="#313650" />
    
    {/* Toggles */}
    <rect x="260" y="130" width="90" height="35" rx="8" fill="#FFB347" opacity="0.9" />
    <circle cx="280" cy="147.5" r="8" fill="#fff" />
    
    <rect x="260" y="175" width="90" height="35" rx="8" fill="#3CB371" opacity="0.9" />
    <circle cx="280" cy="192.5" r="8" fill="#fff" />
    
    <rect x="260" y="220" width="90" height="35" rx="8" fill="#ff5252" opacity="0.9" />
    
    {/* Grid mock */}
    <g transform="translate(60, 60)">
      <rect x="0" y="0" width="160" height="180" rx="10" fill="#0D0F18" stroke="#3A4060" />
      <g fill="#2A2E45">
        <rect x="20" y="20" width="30" height="30" rx="4" />
        <rect x="65" y="20" width="30" height="30" rx="4" fill="#1E88E5" />
        <rect x="110" y="20" width="30" height="30" rx="4" />
        
        <rect x="20" y="65" width="30" height="30" rx="4" fill="#FF8C00"/>
        <rect x="65" y="65" width="30" height="30" rx="4" />
        <rect x="110" y="65" width="30" height="30" rx="4" />
        
        <rect x="20" y="110" width="30" height="30" rx="4" />
        <rect x="65" y="110" width="30" height="30" rx="4" />
        <rect x="110" y="110" width="30" height="30" rx="4" fill="#3CB371" />
      </g>
    </g>
  </svg>
);

const FeatureRow = ({ title, children, SVGComp, reverse }) => {
  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: reverse ? 'row-reverse' : 'row',
      alignItems: 'center', 
      gap: '60px',
      marginBottom: '100px',
      flexWrap: 'wrap'
    }}>
      <div style={{ flex: '1 1 400px' }}>
        <h3 style={{ fontSize: '2.5rem', marginBottom: '20px', color: '#fff', fontWeight: 'bold' }}>{title}</h3>
        <div style={{ fontSize: '1.2rem', color: 'var(--text-muted)', lineHeight: '1.9' }}>{children}</div>
      </div>
      <div style={{ flex: '1 1 400px', borderRadius: '20px', overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.5)' }}>
        <SVGComp />
      </div>
    </div>
  );
};


export default function LandingPage({ onJoinRoom, onCreateRoom }) {
  const [joinId, setJoinId] = useState('');
  const [mode, setMode] = useState(null);
  const [createdRoomInfo, setCreatedRoomInfo] = useState(null);

  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);
  }, []);

  const handleCreate = (selectedMode) => {
    onCreateRoom({ mode: selectedMode }, (info) => {
      setCreatedRoomInfo(info);
      setMode('create_success');
      if (info && info.roomId && info.pin) {
        localStorage.setItem(`pin_${info.roomId}`, info.pin);
      }
    });
  };

  const handleJoin = (e) => {
    e.preventDefault();
    if (joinId.trim()) {
      onJoinRoom(joinId.trim().toUpperCase());
    }
  };

  return (
    <div className="app-container" style={{ display: 'block', overflowY: 'auto', background: 'var(--bg)', fontFamily: 'Tajawal, sans-serif' }}>
      
      {/* ── Hero Section ── */}
      <div style={{ 
        padding: '100px 20px 80px 20px', 
        minHeight: '80vh', 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'radial-gradient(ellipse at top, rgba(30,136,229,0.15), transparent 70%)',
        borderBottom: '1px solid rgba(255,255,255,0.05)'
      }}>
        <div className="landing-card" style={{
          background: 'rgba(20, 24, 38, 0.85)',
          padding: '50px 40px',
          borderRadius: '24px',
          boxShadow: '0 25px 60px rgba(0,0,0,0.7)',
          textAlign: 'center',
          maxWidth: '480px',
          width: '100%',
          border: '1px solid rgba(255,255,255,0.1)',
          backdropFilter: 'blur(10px)'
        }}>
          <h1 style={{ marginBottom: '15px', fontSize: '4rem', background: 'linear-gradient(45deg, #FFB347, #3CB371, #1E88E5)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontWeight: 900 }}>
            حروف
          </h1>
          <p style={{ color: 'var(--text-muted)', marginBottom: '40px', fontSize: '1.2rem', lineHeight: 1.6 }}>
            المنصة الأولى لإدارة المسابقات التفاعلية.<br/>إعدادات مرنة، سرعة استجابة، وتحدي لا ينتهي.
          </p>

          {!mode && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <button 
                className="btn btn-mode" 
                style={{ background: '#FF8C00', color: '#fff', fontSize: '1.2rem', padding: '16px', borderRadius: '12px' }}
                onClick={() => handleCreate('2p')}
              >
                ➕ جلسة فريقين (كلاسيك)
              </button>
              <button 
                className="btn btn-mode" 
                style={{ background: '#2E8B57', color: '#fff', fontSize: '1.2rem', padding: '16px', borderRadius: '12px' }}
                onClick={() => handleCreate('3p')}
              >
                ➕ جلسة 3 فرق
              </button>
              <button 
                className="btn btn-mode" 
                style={{ background: '#8A2BE2', color: '#fff', fontSize: '1.2rem', padding: '16px', borderRadius: '12px' }}
                onClick={() => handleCreate('4p')}
              >
                ➕ جلسة 4 فرق (زوايا)
              </button>
              <button 
                className="btn btn-mode" 
                style={{ fontSize: '1.2rem', padding: '16px', marginTop: '15px', background: 'rgba(0,0,0,0.4)', borderRadius: '12px' }}
                onClick={() => setMode('join')}
              >
                🔗 الانضمام لجلسة قائمة
              </button>
            </div>
          )}

          {mode === 'join' && (
            <form onSubmit={handleJoin} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <input 
                type="text" 
                placeholder="أدخل رمز الجلسة (مثال: ABCD12)" 
                value={joinId}
                onChange={e => setJoinId(e.target.value)}
                style={{
                  padding: '18px',
                  borderRadius: '12px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(0,0,0,0.6)',
                  color: '#fff',
                  fontSize: '1.3rem',
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  outline: 'none'
                }}
                autoFocus
              />
              <button type="submit" className="btn btn-mode" style={{ background: '#1E88E5', color: '#fff', padding: '16px', borderRadius: '12px', fontSize: '1.2rem' }}>
                دخول
              </button>
              <button type="button" className="btn" onClick={() => setMode(null)} style={{ marginTop: '10px', background: 'transparent', color: 'var(--text-muted)' }}>
                رجوع
              </button>
            </form>
          )}

          {mode === 'create_success' && createdRoomInfo && (
            <div style={{ textAlign: 'center', background: 'rgba(0,0,0,0.4)', padding: '30px 20px', borderRadius: '16px', border: '1px solid rgba(76, 175, 80, 0.3)' }}>
              <h2 style={{ color: '#4caf50', marginBottom: '20px', fontSize: '1.8rem' }}>✅ تم توليد الغرفة</h2>
              
              <div style={{ marginBottom: '25px' }}>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: 8 }}>🔗 رمز الجلسة للمتابعين:</div>
                <div style={{ fontSize: '2.5rem', letterSpacing: '8px', fontWeight: 'bold', color: '#FFB347' }}>
                  {createdRoomInfo.roomId}
                </div>
              </div>
              
              <div style={{ marginBottom: '30px', background: 'rgba(255,0,0,0.15)', padding: '20px', borderRadius: '12px' }}>
                <div style={{ color: '#ffaaaa', fontSize: '0.9rem', marginBottom: '10px' }}>⚠️ الرقم السري لإدارة اللوحة:</div>
                <div style={{ fontSize: '2.2rem', letterSpacing: '10px', fontWeight: 'bold', color: '#fff' }}>
                  {createdRoomInfo.pin}
                </div>
              </div>
              
              <button 
                className="btn btn-mode" 
                style={{ background: '#2E8B57', color: '#fff', width: '100%', padding: '16px', fontSize: '1.2rem', borderRadius: '12px' }}
                onClick={() => onJoinRoom(createdRoomInfo.roomId)}
              >
                دخول كمدير الآن
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Product Drill-down Sections ── */}
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '100px 20px' }}>
        
        <FeatureRow 
          title="كيف تفوز؟ (شرح أنظمة اللعب) 🏆"
          SVGComp={WinConditionSVG}
          reverse={false}
        >
          <p style={{ marginBottom: '15px' }}>الهدف الأساسي بطبيعة الحال: بناء جسر متصل من الحروف الملونة يربط بين ضفتي الحلبة المخصصتين لك. النظام مبرمج ليعلن الفائز تلقائياً بمجرد إغلاق المسار!</p>
          <ul style={{ paddingRight: '20px', listStyleType: 'disc' }}>
            <li style={{ marginBottom: '15px' }}><strong style={{ color: '#FFB347' }}>جلسة الفريقين (كلاسيك):</strong> شبكة مستطيلة. البرتقالي يحاول التوصيل من الأعلى للأسفل، والأخضر من اليمين لليسار.</li>
            <li style={{ marginBottom: '15px' }}><strong style={{ color: '#1E88E5' }}>جلسة الـ 3 فرق:</strong> شبكة سداسية دائرية! كل فريق مخصص له ثلث أطراف الدائرة ويحاول الوصول لثلثه المقابل.</li>
            <li style={{ marginBottom: '15px' }}><strong style={{ color: '#D4A3FF' }}>جلسة الـ 4 فرق (الزوايا):</strong> التحدي الأصعب! البرتقالي ضد البنفسجي (أعلى وأسفل)، والأخضر ضد الأزرق (يمين ويسار). الهدف هو قطع الطريق على فريقين في وقت واحد!</li>
          </ul>
        </FeatureRow>

        <FeatureRow 
          title="زر السرعة المدمج بالموبايل 🚨"
          SVGComp={BuzzerSVG}
          reverse={true}
        >
          <p style={{ marginBottom: '15px' }}>حوّل هواتف المتسابقين لأزرار سرعة (Buzzers) حقيقية بدقة عالية، وتخلص من الأجهزة الخارجية!</p>
          <ul style={{ paddingRight: '20px', listStyleType: 'disc' }}>
            <li style={{ marginBottom: '15px' }}><strong>للمتسابقين:</strong> يمكنهم مسح الباركود السري (الموجود داخل مساحة المدير فقط)، واختيار فريقهم لتتحول شاشتهم لزر أحمر عملاق جاهز للكبث!</li>
            <li style={{ marginBottom: '15px' }}><strong>حسم وسرعة:</strong> بمجرد أن يضغط أول متسابق، يُقفل النظام على البقية خلال أجزاء من الميلي-ثانية (عبر Redis Locks)، ويُعلن اسم الفريق الأسرع لكل الشاشات والجمهور.</li>
            <li style={{ marginBottom: '15px' }}><strong>إعادة التفعيل:</strong> بضغطة زر واحدة من شاشة المدير، يتم مسح النتيجة وفتح الزر من جديد للسؤال التالي.</li>
          </ul>
        </FeatureRow>

        <FeatureRow 
          title="أدوات إدارة الجلسة للمدير 🎛️"
          SVGComp={ControlSVG}
          reverse={false}
        >
          <p style={{ marginBottom: '15px' }}>كمدير للعبة، أنت تمتلك لوحة تحكم تفتح عبر زر "إعدادات"، وتقدم لك الخيارات التالية للتحكم الكامل بسير اللعب:</p>
          <ul style={{ paddingRight: '2px', listStyleType: 'none', paddingLeft: 0 }}>
            <li style={{ marginBottom: '15px' }}><strong style={{ color: '#fff' }}>📋 باركود المتسابقين السري:</strong> لا يراه سوى المدير، اعرضه للفرق لتفعيل زر السرعة في هواتفهم.</li>
            <li style={{ marginBottom: '15px' }}><strong style={{ color: '#FFB347' }}>🧹 تصفير الألوان فقط:</strong> يمسح الألوان مع <strong>إبقاء الحروف في مكانها</strong>. مفيد إذا تراجع فريق عن إجابته أو أخطأ.</li>
            <li style={{ marginBottom: '15px' }}><strong style={{ color: '#3CB371' }}>🎲 تصفير وخلط الحروف:</strong> يمسح الألوان ويقوم بتوزيع <strong>وجبة حروف جديدة عشوائياً</strong> على الخانات. مفيد لبدء سؤال وتحدي جديد.</li>
            <li style={{ marginBottom: '15px' }}><strong style={{ color: '#ff5252' }}>⚠️ إعادة شاملة:</strong> خيار جذري؛ يقوم بمسح الجلسة وبناء شبكة جديدة وإصدار <strong>رقم سري (PIN) جديد</strong> لضمان أمان الغرفة وطرد أي متطفلين.</li>
          </ul>
        </FeatureRow>

      </div>
      
      {/* ── Call to Action / Footer ── */}
      <div style={{ textAlign: 'center', padding: '60px 20px', background: 'rgba(0,0,0,0.5)', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <h2 style={{ fontSize: '2rem', color: '#fff', marginBottom: '20px' }}>مستعد لبدء التحدي؟</h2>
        <p style={{ color: 'var(--text-muted)', marginBottom: '30px', fontSize: '1.2rem' }}>اختر حجم ولون فريقك الآن وانطلق في مسابقة لا مثيل لها.</p>
        <button 
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} 
          style={{ background: '#1E88E5', color: '#fff', border: 'none', padding: '15px 40px', fontSize: '1.2rem', borderRadius: '30px', cursor: 'pointer', fontWeight: 'bold' }}
        >
          العودة للأعلى
        </button>
      </div>

    </div>
  );
}
